import { useState, useCallback, useRef } from 'react';
import { useOpsService, mapToReconcilerExecution, executionFromResource } from '../../../shared/api/opsService';
import type { ReconcilerResource } from '../../../types/backend';
import type {
  AsyncDomainSummary,
  ReconcilerExecution,
  ReconcilerStatus,
} from '../../../types/frontend';

export interface KernelViewState {
  reconcilerNames: string[];
  reconcilerStatuses: Record<string, ReconcilerStatus>;
  selectedExecutions: ReconcilerExecution[];
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  refreshList: () => void;
  loadDetail: (domain: string) => void;
}

export function useKernelView(): KernelViewState {
  const ops = useOpsService();
  const [reconcilerNames, setReconcilerNames] = useState<string[]>([]);
  const [reconcilerStatuses, setReconcilerStatuses] = useState<Record<string, ReconcilerStatus>>({});
  const [selectedExecutions, setSelectedExecutions] = useState<ReconcilerExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentDomain = useRef<string | null>(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reconcilers: AsyncDomainSummary[] = await ops.listReconcilers();
      setReconcilerNames(reconcilers.map((r) => r.domain_type));

      const statusResults = await Promise.allSettled(
        reconcilers.map((r) => ops.getReconcilerStatus(r.domain_type))
      );

      const statuses: Record<string, ReconcilerStatus> = {};
      for (let i = 0; i < reconcilers.length; i++) {
        const result = statusResults[i];
        if (result.status === 'fulfilled') {
          statuses[reconcilers[i].domain_type] = result.value;
        }
      }
      setReconcilerStatuses(statuses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reconcilers');
    } finally {
      setLoading(false);
    }
  }, [ops]);

  const loadDetail = useCallback(async (domain: string) => {
    currentDomain.current = domain;
    setDetailLoading(true);
    try {
      const resources: ReconcilerResource[] = await ops.listReconcilerResources(domain);
      const resourcesWithExec = resources.filter((r) => r.execution_id);

      const detailResults = await Promise.allSettled(
        resourcesWithExec.map((r) =>
          ops.getExecutionDetail(r.execution_id!).then((detail) =>
            mapToReconcilerExecution(detail, r)
          )
        )
      );

      if (currentDomain.current !== domain) return;

      const mapped: ReconcilerExecution[] = detailResults.map((result, i) =>
        result.status === 'fulfilled'
          ? result.value
          : executionFromResource(
              resourcesWithExec[i],
              'Could not load run detail',
              result.reason instanceof Error ? result.reason.message : String(result.reason),
            ),
      );

      mapped.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
      setSelectedExecutions(mapped);
    } catch {
      if (currentDomain.current === domain) {
        setSelectedExecutions([]);
      }
    } finally {
      if (currentDomain.current === domain) {
        setDetailLoading(false);
      }
    }
  }, [ops]);

  return {
    reconcilerNames,
    reconcilerStatuses,
    selectedExecutions,
    loading,
    detailLoading,
    error,
    refreshList,
    loadDetail,
  };
}
